<script setup lang="ts">
import { computed, useId } from 'vue';

interface Props {
  label?: string;
  /**
   * Appends the required marker to the label. It does not set the native
   * `required` attribute, whose browser validation would collide with the Zod
   * resolvers the forms use — controls take `aria-required` from the slot.
   */
  required?: boolean;
  /**
   * Hint under the control. Superseded by `error` while the field is invalid.
   */
  helper?: string;
  /**
   * Validation message. Its presence also marks the field invalid.
   */
  error?: string;
  /**
   * Marks the field invalid without its own message, for group-level errors.
   */
  invalid?: boolean;
  /**
   * Overrides the generated control id when a caller has to address the field.
   */
  inputId?: string;
}

const { helper, error, inputId, invalid } = defineProps<Props>();

defineSlots<{
  /**
   * The control. Wire the three bindings onto it so the label, the message and
   * the invalid state stay connected.
   */
  default(props: { inputId: string; describedBy: string | undefined; invalid: boolean }): unknown;
}>();

const generatedId = useId();
const fieldId = computed(() => inputId ?? generatedId);
const messageId = computed(() => `${fieldId.value}-message`);
const message = computed(() => error ?? helper);
</script>

<template>
  <div class="ui-field">
    <label v-if="label" class="ui-field__label" :for="fieldId">
      {{ label }}
      <span v-if="required" class="ui-field__required" aria-hidden="true">*</span>
    </label>

    <slot
      :inputId="fieldId"
      :describedBy="message ? messageId : undefined"
      :invalid="invalid || !!error"
    />

    <small
      v-if="message"
      :id="messageId"
      class="ui-field__message"
      :class="{ 'ui-field__message--error': !!error }"
    >
      {{ message }}
    </small>
  </div>
</template>

<style scoped lang="scss">
.ui-field {
  display: flex;
  flex-direction: column;
  gap: $spacing-xs;

  &__label {
    font-size: $font-size-sm;
    font-weight: $font-weight-medium;
    color: $color-text-secondary;
  }

  &__required {
    color: $color-state-danger;
    margin-left: $spacing-2xs;
  }

  &__message {
    font-size: $font-size-xs;
    line-height: $line-height-xs;
    color: $color-text-secondary;

    &--error {
      color: $color-state-danger;
    }
  }
}
</style>
